import {
  CHART_CALCULATION,
  CHART_COLOR_SCALE,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_FREQUENCY,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { Dashboard2ColumnType } from '@/features/Dashboard/types/constants';
import type { FeedbackListItemWithBlock } from '@/types/feedback';

import type { ChartInReport, ReportItem, ReportRowT, ReportT } from '../types';
import type {
  FetchReportListParams,
  ReportCreatePayloadDTO,
  ReportUpdatePayloadDTO,
} from './types';

// Mock Chart Data
const mockChartInReport: ChartInReport = {
  alias: 'chart-001',
  name: 'Customer Satisfaction Trend',
  type: CHART_TYPE.SINGLE_AXIS,
  historical: {
    frequency: CHART_FREQUENCY.MONTH,
    value: true,
  },
  graph: CHART_GRAPH.AREA_STACKED,
  field: {
    type: CHART_FIELD.FEEDBACK_SENTIMENT,
    entire: true,
    candidates: ['positive', 'negative', 'neutral'],
  },
  selected_field: {
    method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
    items: ['positive', 'negative', 'neutral'],
  },
  target: {
    is_compare: false,
    term: {
      type: CHART_TERM_TYPE.PERIODIC,
      relative: CHART_PERIOD_RELATIVE.INCLUDE,
      value: 30,
      unit: CHART_TERM_UNIT.DAY,
    },
  },
  series: [
    {
      measure: {
        metric: CHART_METRIC.NPS_SCORE,
        calculation: CHART_CALCULATION.COUNT,
      },
      options: {
        view_type: CHART_METRIC_VIEW_TYPE.COUNT,
        color_scale: CHART_COLOR_SCALE.WHITE_TO_GREEN,
      },
    },
  ],
};

// Mock Report Items
const mockReportItems: ReportItem[] = [
  {
    alias: 'item-001',
    type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
    chart: mockChartInReport,
  },
  {
    alias: 'item-003',
    type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
    text: 'This is a summary of our Q4 customer feedback analysis. The data shows significant improvements in customer satisfaction.',
  },
];

// Mock Report Rows
const mockReportRows: ReportRowT[] = [
  {
    alias: 'row-001',
    height: 400,
    columns: [
      {
        alias: 'col-001',
        item_alias: 'item-001',
      },
      {
        alias: 'col-002',
        item_alias: 'item-002',
      },
    ],
  },
  {
    alias: 'row-002',
    height: 200,
    columns: [
      {
        alias: 'col-003',
        item_alias: 'item-003',
      },
    ],
  },
];

// Mock Reports
const mockReports: ReportT[] = [
  {
    alias: 'report-001',
    title: 'Q4 Customer Feedback Report',
    created_by: 'user-001',
    created_at: '2024-01-15T10:30:00Z',
    updated_by: 'user-001',
    updated_at: '2024-01-16T14:20:00Z',
    items: mockReportItems,
    rows: mockReportRows,
  },
  {
    alias: 'report-002',
    title: 'Monthly Product Performance',
    created_by: 'user-002',
    created_at: '2024-01-10T09:15:00Z',
    updated_by: 'user-002',
    updated_at: '2024-01-12T16:45:00Z',
    items: [
      {
        alias: 'item-004',
        type: Dashboard2ColumnType.DASHBOARD_COLUMN_CHART,
        chart: {
          alias: 'chart-002',
          name: 'Product Performance Metrics',
          type: CHART_TYPE.SINGLE_AXIS,
          historical: {
            frequency: CHART_FREQUENCY.WEEK,
            value: true,
          },
          graph: CHART_GRAPH.BAR,
          field: {
            type: CHART_FIELD.FEEDBACK_RATING_SCORE,
            entire: false,
            candidates: ['1', '2', '3', '4', '5'],
          },
          selected_field: {
            method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_10,
            items: ['4', '5'],
          },
          target: {
            is_compare: true,
            term: {
              type: CHART_TERM_TYPE.PERIODIC,
              relative: CHART_PERIOD_RELATIVE.EXCLUDE,
              value: 7,
              unit: CHART_TERM_UNIT.WEEK,
            },
          },
          series: [
            {
              measure: {
                metric: CHART_METRIC.UNIQUE_CONTACT,
                calculation: CHART_CALCULATION.AVERAGE,
              },
              options: {
                view_type: CHART_METRIC_VIEW_TYPE.PERCENTAGE,
                color_scale: CHART_COLOR_SCALE.GREEN_TO_RED,
              },
            },
          ],
        },
      },
    ],
    rows: [
      {
        alias: 'row-003',
        height: 500,
        columns: [
          {
            alias: 'col-004',
            item_alias: 'item-004',
          },
        ],
      },
    ],
  },
  {
    alias: 'report-003',
    title: 'Weekly Insights Summary',
    created_by: 'user-003',
    created_at: '2024-01-08T11:00:00Z',
    updated_by: 'user-001',
    updated_at: '2024-01-14T13:30:00Z',
    items: [
      {
        alias: 'item-005',
        type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
        text: 'Weekly summary of key insights and trends from customer data.',
      },
    ],
    rows: [
      {
        alias: 'row-004',
        height: 150,
        columns: [
          {
            alias: 'col-005',
            item_alias: 'item-005',
          },
        ],
      },
    ],
  },
];

// Mock API Responses
export const mockReportAPIResponses = {
  // fetchReportList response
  fetchReportList: {
    data: mockReports,
    totalCount: 3,
  },

  // fetchReport response (single report)
  fetchReport: mockReports[0],

  // createReport response (newly created report)
  createReport: {
    alias: 'report-004',
    title: 'New Analytics Report',
    created_by: 'user-004',
    created_at: '2024-01-17T12:00:00Z',
    updated_by: 'user-004',
    updated_at: '2024-01-17T12:00:00Z',
    items: [
      {
        alias: 'item-006',
        type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
        text: 'This is a newly created report.',
      },
    ],
    rows: [
      {
        alias: 'row-005',
        height: 300,
        columns: [
          {
            alias: 'col-006',
            item_alias: 'item-006',
          },
        ],
      },
    ],
  },

  // updateReport response (updated report)
  updateReport: {
    ...mockReports[0],
    title: 'Updated Q4 Customer Feedback Report',
    updated_by: 'user-005',
    updated_at: '2024-01-17T15:30:00Z',
  },

  // deleteReport response (void - no response body)
  deleteReport: undefined,
};

// Mock service that mirrors the real reportService API
export const mockReportService = {
  fetchReportList: async (
    params?: FetchReportListParams,
  ): Promise<{
    data: ReportT[];
    count: number;
  }> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Simulate pagination
    const { limit = 10, offset = 0 } = params || {};
    const startIndex = offset;
    const endIndex = startIndex + limit;
    const paginatedData = mockReports.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      count: mockReports.length,
    };
  },

  fetchReport: async (alias: string): Promise<ReportT> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Find report by alias or return the first one
    const report = mockReports.find((r) => r.alias === alias) || mockReports[0];

    if (!report) {
      throw new Error(`Report with alias ${alias} not found`);
    }

    return report;
  },

  createReport: async (data: ReportCreatePayloadDTO): Promise<ReportT> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    // Create new report with mock data structure
    const newReport: ReportT = {
      alias: `report-${Date.now()}`,
      title: data.title,
      created_by: 'current-user',
      created_at: new Date().toJSON(),
      updated_by: 'current-user',
      updated_at: new Date().toJSON(),
      items: data.items || [],
      rows: data.rows || [],
    };

    return newReport;
  },

  updateReport: async (
    alias: string,
    data: ReportUpdatePayloadDTO,
  ): Promise<ReportT> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 600));

    // Find existing report and update it
    const existingReport =
      mockReports.find((r) => r.alias === alias) || mockReports[0];

    const updatedReport: ReportT = {
      ...existingReport,
      ...data,
      alias, // Keep original alias
      updated_by: 'current-user',
      updated_at: new Date().toJSON(),
    };

    return updatedReport;
  },

  deleteReport: async (alias: string): Promise<void> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 400));

    // In a real scenario, you might want to check if the report exists
    const reportExists = mockReports.some((r) => r.alias === alias);

    if (!reportExists) {
      throw new Error(`Report with alias ${alias} not found`);
    }

    // Mock delete - no return value
    return;
  },
  async fetchAnalysisFeedbackList(
    report_alias: string,
    analysis_alias: string,
    answer_alias: string,
  ): Promise<FeedbackListItemWithBlock[]> {
    return [];
  },
  async fetchChartTable(
    report_alias: string,
    chart_alias: string,
  ): Promise<ChartTableRes> {
    return {
      tables: [],
      field_item_meta: [],
    };
  },
};

// Export individual mock data for flexible usage
export { mockChartInReport, mockReportItems, mockReportRows, mockReports };
